import { computed } from 'vue'

import type { Country } from '@backmarket/http-api/src/standards/Country'
import { useDynamicAddressFieldValidators } from '@backmarket/nuxt-module-address/useDynamicAddressFieldValidators'
import { FORM_VALID, type Validator, makeValidate } from '@ds/components/Form'

export type DeliveryPostalCodeFormValues = {
  postalCode: string
  // Required by getDynamicAddressFieldValidators — it reads `values.country` to pick the
  // country-specific rules.
  country: string
}

type PostalCodeValidator = Validator<DeliveryPostalCodeFormValues>

export function useDeliveryPostalCodeValidate(country: string) {
  const { getDynamicAddressFieldValidatorsWithAsyncData } =
    useDynamicAddressFieldValidators()

  const { data: validators } = getDynamicAddressFieldValidatorsWithAsyncData({
    initialFormValues: { postalCode: '', country: country as Country },
  })

  const postalCodeRules = computed(
    () =>
      (validators.value?.postalCode as unknown as PostalCodeValidator) ?? null,
  )

  return {
    validate: makeValidate<DeliveryPostalCodeFormValues>({
      // An empty value is allowed: submitting it clears the postal code and
      // returns to the default state. Country rules only run on a real value.
      postalCode: [
        (value, values, context) =>
          typeof value === 'string' && value.trim()
            ? (postalCodeRules.value?.(value, values, context) ?? FORM_VALID)
            : FORM_VALID,
      ],
    }),
  }
}
